/**
 * Project Page - Converted from Next.js
 */

import { useState } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router"
import { useProject, useUpdateProject, useDeleteProject } from "@/hooks/use-projects"
import { useGoals, usePersonas, useInterviews, useCompetitors, useJourneys, useUseCases, useDocuments } from "@/hooks/use-artifacts"
import { useToast } from "@/hooks/use-toast"
import { ProjectHeader } from "@/components/layout/project-header"
import { ProjectSidebar } from "@/components/layout/project-sidebar"
import { ProductGuruPanel } from "@/components/guru/product-guru-panel"
import { ChatsPanel } from "@/components/chats"
import { KnowledgePanel } from "@/components/knowledge"
import { ProjectOverviewPanel } from "@/components/overview"
import { DiscoveryPanel } from "@/components/discovery"
import { DefinitionPanel } from "@/components/definition"
import { DesignPanel } from "@/components/design"
import { DeliveryPanel } from "@/components/delivery"
import { BaseSpecEditor } from "@/components/base-specs"
import { ContractEditor } from "@/components/contracts"
import { TechnicalSpecEditor } from "@/components/technical-specs"
import { EvidencePanel } from "@/components/evidence"
import { ReleasePanel } from "@/components/releases"
import { TraceabilityGraph } from "@/components/traceability"
import { ApprovalDashboard } from "@/components/approvals"
import { AuditLogPanel } from "@/components/audit-log"
import { AddTemplateDialog } from "@/components/templates"
import { EditProjectDialog, type ProjectEditData } from "@/components/projects/edit-project-dialog"
import { ManageMembersDialog } from "@/components/projects/manage-members-dialog"
import { ComingSoonPanel } from "@/components/shared"
import { Skeleton } from "@/components/ui/skeleton"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { isRouteDisabled, getDisabledSectionDisplayName } from "@/lib/feature-flags"

// Valid section IDs - includes all main sections and sub-sections
const VALID_SECTIONS = [
  "guru", 
  "chats", 
  "knowledge", 
  "overview",
  // Discovery sub-sections
  "discovery",
  "discovery-goals", 
  "discovery-interviews", 
  "discovery-competitors",
  // Definition sub-sections
  "definition",
  "definition-personas", 
  "definition-usecases", 
  "definition-servicemap",
  "definition-journeys", 
  "definition-storyboards",
  // Design sub-sections
  "design",
  "design-architecture", 
  "design-areas",
  // Delivery sub-sections
  "delivery",
  "delivery-architecture",
  "delivery-storymap",
  "delivery-mvp",
  "delivery-stories",
  // Additional sections
  "base-specs",
  "contracts",
  "technical-specs",
  "evidence",
  "releases",
  "traceability",
  "approvals",
  "audit-log",
]

export default function ProjectPage() {
  const { id: projectId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { toast } = useToast()
  
  // Get active section from URL, default to "guru"
  const sectionFromUrl = searchParams.get("tab") || "guru"
  const activeSection = VALID_SECTIONS.includes(sectionFromUrl) ? sectionFromUrl : "guru"
  
  // Fetch project data from API
  const { data: project, isLoading: projectLoading, error: projectError } = useProject(projectId!)
  const updateProjectMutation = useUpdateProject()
  const deleteProjectMutation = useDeleteProject()
  
  // Fetch artifacts for the current project
  const { data: goals = [] } = useGoals(projectId!)
  const { data: personas = [] } = usePersonas(projectId!)
  const { data: interviews = [] } = useInterviews(projectId!)
  const { data: competitors = [] } = useCompetitors(projectId!)
  const { data: journeys = [] } = useJourneys(projectId!)
  const { data: useCases = [] } = useUseCases(projectId!)
  const { data: documents = [] } = useDocuments(projectId!)
  
  const [isAddTemplateOpen, setIsAddTemplateOpen] = useState(false)
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false)
  const [isManageMembersOpen, setIsManageMembersOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const handleSidebarItemClick = (id: string) => {
    // Update URL with the new tab
    setSearchParams({ tab: id })
  }

  const handleAddTemplate = (template: { id: string; name: string; type: string; category: string }) => {
    const sectionMap: Record<string, string> = {
      GOAL: "discovery-goals",
      INTERVIEW: "discovery-interviews",
      COMPETITOR_ANALYSIS: "discovery-competitors",
      PERSONA: "definition-personas",
      USE_CASE: "definition-usecases",
      JOURNEY: "definition-journeys",
      SERVICE_MAP: "definition-servicemaps",
      STORYBOARD: "definition-storyboards",
      BASE_SPEC: "base-specs",
      TECHNICAL_SPEC: "technical-specs",
      STORY: "delivery-stories",
      PROJECT_OVERVIEW: "overview",
    }
    const section = sectionMap[template.type]
    if (section) {
      setSearchParams({ tab: section })
      toast({
        title: template.name,
        description: "Click 'Generate' to create content using this template.",
      })
    }
    setIsAddTemplateOpen(false)
  }

  const handleEditProject = async (data: ProjectEditData) => {
    try {
      await updateProjectMutation.mutateAsync({
        id: projectId!,
        name: data.name,
        description: data.description,
        deadline: data.deadline,
        emoji: data.emoji,
      })
      toast({
        title: "Project Updated",
        description: "Your changes have been saved.",
        variant: "success",
      })
      setIsEditProjectOpen(false)
    } catch (error) {
      toast({
        title: "Update Failed",
        description: error instanceof Error ? error.message : "Failed to update project",
        variant: "destructive",
      })
    }
  }

  const handleDeleteProject = async () => {
    try {
      await deleteProjectMutation.mutateAsync(projectId!)
      toast({
        title: "Project Deleted",
        description: "The project has been archived successfully.",
        variant: "success",
      })
      setIsDeleteDialogOpen(false)
      navigate("/dashboard")
    } catch (error) {
      toast({
        title: "Delete Failed",
        description: error instanceof Error ? error.message : "Failed to delete project",
        variant: "destructive",
      })
    }
  }

  // Note: handleAskAI is no longer needed - Design and Delivery panels handle AI generation internally
  // Discovery panels also handle it internally via generate mutation
  // This stub is kept for backward compatibility but is not used
  const handleAskAI = async (message: string) => {
    console.warn("handleAskAI called but not implemented - panels should handle AI generation internally")
  }

  // Loading state - use containers matching loaded panel structure
  if (projectLoading) {
    return (
      <div className="min-h-screen bg-surface-secondary flex flex-col">
        {/* Header - matches ProjectHeader structure exactly */}
        <header className="shrink-0 bg-white border-b border-surface-border h-[93px]">
          <div className="h-[92px] w-full px-6 flex items-center justify-between">
            {/* Left: Back + Icon + Name/Type */}
            <div className="flex items-center gap-4">
              <Skeleton className="w-6 h-6 rounded shrink-0" />
              <Skeleton className="w-10 h-10 rounded-[10px] shrink-0" />
              <div className="space-y-2">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-20 rounded-[10px]" />
              <Skeleton className="h-10 w-16 rounded-[10px]" />
              <Skeleton className="w-9 h-9 rounded-[10px]" />
            </div>
          </div>
        </header>
        <div className="flex-1 flex w-full min-h-0">
          {/* Sidebar - matches project sidebar */}
          <div className="w-[320px] shrink-0 bg-white border-r border-surface-border p-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="mb-2 p-2 rounded-lg border border-gray-100">
                <Skeleton className="w-full h-4" />
                <Skeleton className="w-2/3 h-3 mt-1" />
              </div>
            ))}
          </div>
          {/* Main content - matches Product Guru / panel layout with proper containers */}
          <div className="flex-1 overflow-auto flex flex-col min-h-0">
            {/* Panel header */}
            <div className="shrink-0 bg-white px-8 py-6 border-b border-surface-border">
              <div className="flex items-center gap-2 mb-2">
                <Skeleton className="h-6 w-6 rounded" />
                <Skeleton className="h-5 w-32" />
              </div>
              <Skeleton className="h-4 w-full max-w-md" />
            </div>
            {/* Panel content - cards in container */}
            <div className="flex-1 overflow-auto bg-surface-secondary px-8 py-8">
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="bg-white border border-surface-border rounded-[14px] p-5 shadow-card min-h-[236px] flex flex-col"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Skeleton className="h-5 w-5 rounded-full shrink-0" />
                      <Skeleton className="h-5 w-20 rounded-full" />
                      <Skeleton className="h-5 w-24 rounded-full" />
                    </div>
                    <Skeleton className="h-5 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-full mb-1" />
                    <Skeleton className="h-4 w-full mb-1" />
                    <Skeleton className="h-4 w-2/3 mb-4" />
                    <div className="flex gap-2 pt-3 border-t border-surface-border mt-auto">
                      <Skeleton className="h-8 w-16 rounded-lg" />
                      <Skeleton className="h-8 w-14 rounded-lg" />
                      <Skeleton className="h-8 w-20 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (projectError || !project) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-medium text-gray-900 mb-2">Project Not Found</h2>
          <p className="text-gray-600 mb-4">
            {projectError?.message || "The project you are looking for does not exist."}
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="text-brand-primary hover:underline"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    )
  }

  // Render the appropriate panel based on active section
  const renderPanel = () => {
    // Check if the current section is disabled (coming soon)
    if (isRouteDisabled(activeSection)) {
      return (
        <ComingSoonPanel 
          featureName={getDisabledSectionDisplayName(activeSection)} 
        />
      );
    }
    
    switch (activeSection) {
      case "chats":
        return <ChatsPanel projectId={projectId!} projectName={project.name} />
      case "guru":
        return (
          <ProductGuruPanel 
            projectId={projectId!}
            onRunAnalysis={() => {
              toast({
                title: "Running Analysis",
                description: "AI is analyzing your project...",
              })
            }}
          />
        )
      case "knowledge":
        return (
          <KnowledgePanel 
            projectId={projectId!}
            projectName={project.name}
            documents={documents}
          />
        )
      case "overview":
        return (
          <ProjectOverviewPanel 
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "discovery-goals":
        return (
          <DiscoveryPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="goals"
            goals={goals}
            onAskAI={handleAskAI}
          />
        )
      case "discovery-interviews":
        return (
          <DiscoveryPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="interviews"
            interviews={interviews}
            onAskAI={handleAskAI}
          />
        )
      case "discovery-competitors":
        return (
          <DiscoveryPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="competitors"
            competitors={competitors}
            onAskAI={handleAskAI}
          />
        )
      case "definition":
      case "definition-personas":
        return (
          <DefinitionPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="personas"
          />
        )
      case "definition-usecases":
        return (
          <DefinitionPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="use-cases"
          />
        )
      case "definition-servicemap":
        return (
          <DefinitionPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="service-maps"
          />
        )
      case "definition-journeys":
        return (
          <DefinitionPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="journeys"
          />
        )
      case "definition-storyboards":
        return (
          <DefinitionPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="storyboards"
          />
        )
      case "design":
      case "design-architecture":
        return (
          <DesignPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="architecture"
            onAskAI={handleAskAI}
          />
        )
      case "design-areas":
        return (
          <DesignPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="areas"
            onAskAI={handleAskAI}
          />
        )
      case "delivery-architecture":
        return (
          <DeliveryPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="architecture"
            onAskAI={handleAskAI}
          />
        )
      case "delivery":
      case "delivery-storymap":
        return (
          <DeliveryPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="storymap"
            onAskAI={handleAskAI}
          />
        )
      case "delivery-mvp":
        return (
          <DeliveryPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="mvp"
            onAskAI={handleAskAI}
          />
        )
      case "delivery-stories":
        return (
          <DeliveryPanel 
            projectId={projectId!}
            projectName={project.name}
            activeView="stories"
            onAskAI={handleAskAI}
          />
        )
      case "base-specs":
        return (
          <BaseSpecEditor 
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "contracts":
        return (
          <ContractEditor 
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "technical-specs":
        return (
          <TechnicalSpecEditor 
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "evidence":
        return (
          <EvidencePanel 
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "releases":
        return (
          <ReleasePanel 
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "traceability":
        return (
          <TraceabilityGraph 
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "approvals":
        return (
          <ApprovalDashboard
            projectId={projectId!}
            projectName={project.name}
          />
        )
      case "audit-log":
        return (
          <AuditLogPanel
            projectId={projectId!}
            projectName={project.name}
          />
        )
      default:
        // Unknown tab - redirect to overview instead of showing "coming soon"
        return (
          <ProjectOverviewPanel 
            projectId={projectId!}
            projectName={project.name}
          />
        )
    }
  }

  return (
    <div className="h-screen bg-white flex flex-col overflow-hidden">
      {/* Project Header */}
      <ProjectHeader
        projectName={project.name}
        projectType={project.type}
        projectEmoji={project.emoji}
        onBack={() => navigate("/dashboard")}
        onEdit={() => setIsEditProjectOpen(true)}
        onDelete={() => setIsDeleteDialogOpen(true)}
        onManageMembers={() => setIsManageMembersOpen(true)}
        onToggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        memberCount={project.members?.length || 0}
      />
      
      {/* Main content area */}
      <div className="flex-1 flex overflow-hidden w-full">
        {/* Sidebar - hidden on mobile by default, shown via toggle */}
        <ProjectSidebar 
          projectId={projectId!}
          activeItem={activeSection}
          onItemClick={handleSidebarItemClick}
          onAddTemplate={() => setIsAddTemplateOpen(true)}
          artifactCounts={{
            personas: personas.length,
            journeys: journeys.length,
            useCases: useCases.length,
            documents: documents.length,
          }}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        
        {/* Dynamic Panel - full width on mobile */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {renderPanel()}
        </div>
      </div>

      {/* Dialogs */}
      <AddTemplateDialog
        isOpen={isAddTemplateOpen}
        onClose={() => setIsAddTemplateOpen(false)}
        onAddTemplate={handleAddTemplate}
      />

      <EditProjectDialog
        isOpen={isEditProjectOpen}
        onClose={() => setIsEditProjectOpen(false)}
        onSave={handleEditProject}
        project={{
          id: project.id,
          name: project.name,
          description: project.description || "",
          deadline: project.deadline || "",
          emoji: project.emoji,
          type: project.type,
        }}
      />

      <ManageMembersDialog
        projectId={projectId!}
        projectName={project.name}
        isOpen={isManageMembersOpen}
        onClose={() => setIsManageMembersOpen(false)}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteProject}
        title="Delete Project"
        description={
          <>
            Are you sure you want to delete "{project.name}"?
            <br />
            This project will be archived and can be restored later by an administrator.
          </>
        }
        confirmText="Delete Project"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteProjectMutation.isPending}
      />
    </div>
  )
}

